import type { ReactNode } from 'react';
import classnames from 'classnames';
import { type FieldValues, useForm } from 'react-hook-form';
import { Button, Modal } from 'components/common';
import { Radio } from 'components/forms';
import { useResponsiveWidth, useUser } from 'libs';
import type { ExitSurveyPayload } from './types';

interface ExitSurveyOption {
  commentRequired?: boolean;
  key: number;
  value: string;
}

interface ExitSurveyProps {
  children: ReactNode;
  name: string;
  onDowngrade: (subscriptionType: string, data: ExitSurveyPayload) => void;
  onHideModal: () => void;
  options: ExitSurveyOption[];
  showModal: boolean;
  subscriptionType: string;
}

const ExitSurvey = ({
  children,
  name,
  onDowngrade,
  onHideModal,
  options,
  showModal,
  subscriptionType,
}: ExitSurveyProps) => {
  const user = useUser();
  const { isMinSm } = useResponsiveWidth();
  const { handleSubmit, register, reset, watch } = useForm();

  const selectedOption = watch('reason');
  const selectedOptionRequiresComment = options.find((o) => o.value === selectedOption)?.commentRequired;
  const disabled = !selectedOption || (selectedOptionRequiresComment && !watch('comment'));

  const onSubmit = (data: FieldValues) => {
    onDowngrade(subscriptionType, { name, ...data });
    reset();
  };

  return (
    <Modal centered onHide={onHideModal} show={showModal}>
      <div className="p-3">
        <h3 className="fw-bold" data-testid="subscriptions-downgrade-subscription-modal-title">
          {user.name.split(' ')[0]}, we&apos;re sorry to see you go! &#58;&#40;
        </h3>
        <div className="fw-bold my-4">
          Please tell us the main reason why you&apos;re downgrading your {name} subscription.*
        </div>
        <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid md:grid-cols-2 gap-2" data-testid="subscriptions-downgrade-exit-survey-option">
              {options.map((option) => (
                <Radio
                  id={option.key}
                  key={option.key}
                  label={option.value}
                  register={register('reason')}
                  value={option.value}
                />
              ))}
            </div>
            {selectedOption && selectedOptionRequiresComment ? (
              <div className="fw-bold my-2">Please share a little more. Your feedback is very important to us. *</div>
            ) : (
              <div className="fw-bold my-2">
                Any additional comments or feedback you&apos;d like to share? We read every answer.
              </div>
            )}
            {selectedOption && (
              <textarea
                className="form-control text-0_85"
                data-testid="subscriptions-downgrade-exit-survey-comment"
                id="comment"
                maxLength={255}
                rows={4}
                {...register('comment')}
              />
            )}
            <div className="py-3">
              <div className={classnames({ 'd-inline-block': isMinSm, 'col-md-12 mb-2': !isMinSm })}>
                <Button
                  disabled={disabled}
                  submit
                  testId="subscriptions-downgrade-subscription-modal-confirm-button"
                  text="bold"
                  variant="danger"
                  width={isMinSm ? 'content' : 'block'}
                >
                  {subscriptionType === 'platinum' ? 'Downgrade to Basic' : 'Downgrade my subscription'}
                </Button>
              </div>
              <div className={classnames({ 'd-inline-block ms-2': isMinSm, 'col-md-12': !isMinSm })}>
                <Button onClick={onHideModal} text="bold" variant="border" width={isMinSm ? 'content' : 'block'}>
                  {subscriptionType === 'platinum' ? 'Keep my Platinum subscription' : 'Keep my subscription'}
                </Button>
              </div>
            </div>
          </form>
        </div>
        <div className="pt-1 text-0_75">{children}</div>
      </div>
    </Modal>
  );
};

export default ExitSurvey;
